import React, { useEffect, useRef, useState } from 'react';
import _ from 'lodash';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Dropdown,
  Tooltip,
  InputRef,
  Switch,
} from 'antd';
import { Notify } from '../../services/notificationService';
import { MoreOutlined } from '@ant-design/icons';
import { ColumnsType } from 'antd/es/table';
import UserRoles, { UserRole } from '../../enums/userRoles';
import { ItemType } from 'antd/es/menu/interface';
import {
  OrganizationUserData,
  deleteUser,
  OrganizationData,
  updateUserRole,
  getOrganizationUsers,
  getNameOfRole,
  getTeamName,
  sendInvitation,
  getOrganizationInvitations,
  resendInvitation,
  withdrawInvitation,
  updateTeamSettings,
} from '../../services/account.service';
import { TeamInvitation } from '../../interfaces/team';
import InvitationStatusBadge from '../../components/InvitationStatusBadge/InvitationStatusBadge';
import './UsersScreen.less';
import { TokenService } from '../../services/secureTokenService';
import { getReadonlyElementTitle, getRole } from '../../services/subscription';
import { useSelector } from 'react-redux';
import { RootState } from '../../app/store';
/*
import { OrganizationLicense } from '../../enums/account';
import {
  ADMIN_NOT_AVAILABLE_FOR_GUEST,
  // LICENSE_INFO,
} from '../../constants/texts';
*/
import { confirmAction } from '../../utils/confirmActionModal';
import { quantitizeRole, sortUserRole } from '../../utils/sorters';
import { useParams } from 'react-router-dom';
import { useAbility } from '../../hooks/useAbility';
import { Resource } from '../../enums/common';
import { ROLE_HIERARCHY } from '../../services/abilityService';
import RoleBadge from '../../components/RoleBadge/RoleBadge';

const { Option } = Select;

const UsersScreen: React.FC = () => {
  const teamIdFromUrl = useParams<{ teamId: string }>().teamId;
  const currentTeamInfo = useSelector(
    (state: RootState) => state.team.currentTeam
  );
  const globalLoading = useSelector((state: RootState) => state.global.loading);
  const teamId = teamIdFromUrl || currentTeamInfo?.record?.id || '';

  const { teamAbility } = useAbility();
  const [orgData, setOrgData] = useState<OrganizationData>({
    team: null,
    organization_users: [],
  });
  const [pendingInvitations, setPendingInvitations] = useState<
    TeamInvitation[]
  >([]);
  const [usersLoading, setUsersLoading] = useState<boolean>(true);
  const [teamSwitchInProgress, setTeamSwitchInProgress] =
    useState<boolean>(false);
  const [addUserLoading, setAddUserLoading] = useState<boolean>(false);
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [teamOnlyInvitations, setTeamOnlyInvitations] =
    useState<boolean>(false);
  const [teamOnlyLoading, setTeamOnlyLoading] = useState<boolean>(false);
  const [form] = Form.useForm();
  const teamNameInputRef = useRef<InputRef>(null);

  const isLoggedInAnotherTeam = currentTeamInfo.record?.id !== teamId;
  const generalReadOnlyMsg =
    getReadonlyElementTitle() || isLoggedInAnotherTeam
      ? 'You are not logged in this team. Please switch team first.'
      : '';

  const isOwner = getRole() === UserRoles.OWNER;

  // Check if user has permission to invite users (admin/owner only)
  const hasInvitePermission = teamAbility.can(
    'update',
    Resource.UserInfoAsUser,
    {
      cirra_user_id: 'dummy@example.com', // Dummy user to check general permission
      cirra_organization_id: orgData.team?.cirra_organization_id || teamId,
      role: UserRoles.MEMBER,
    },
    'role'
  );

  let inviteTooltip;
  if (globalLoading || usersLoading) {
    inviteTooltip = globalLoading ? 'Switching teams...' : 'Loading...';
  } else if (generalReadOnlyMsg) {
    inviteTooltip = generalReadOnlyMsg;
  } else if (!hasInvitePermission) {
    inviteTooltip = 'You do not have permission to invite users';
  }
  /*
   else {
    inviteTooltip = isLimitReached ? 'No licenses left' : '';
  }
  */

  // Handle team switching with proper loading states
  useEffect(() => {
    if (globalLoading) {
      setTeamSwitchInProgress(true);
      setUsersLoading(true);
      // Clear data to prevent showing stale information
      setOrgData({
        team: null,
        organization_users: [],
      });
      setPendingInvitations([]);
    } else {
      setTeamSwitchInProgress(false);
    }
  }, [globalLoading]);

  useEffect(() => {
    if (teamId && !globalLoading) {
      // Add a small delay to ensure backend context is updated after team switch
      const timeoutId = setTimeout(
        () => {
          fetchUsers(teamId);
        },
        teamSwitchInProgress ? 500 : 0
      );

      return () => clearTimeout(timeoutId);
    }
  }, [teamId, globalLoading, teamSwitchInProgress]);

  /*
  useEffect(() => {
    setIsLimitReached(
      !orgData.full_users_remaining || orgData.full_users_remaining <= 0
    );
  }, [orgData]);
  */

  useEffect(() => {
    if (isModalVisible && teamNameInputRef.current) {
      // Add a small delay to fix the race condition. Otherwise, auto focus only works one time.
      setTimeout(() => {
        teamNameInputRef.current?.focus();
      }, 100);
    }
  }, [isModalVisible]);

  // Effective settings: child's own values take precedence, otherwise inherit from parent
  const effectivePrimaryDomain =
    orgData.team?.primary_domain ||
    orgData.team?.master_organization?.primary_domain;
  const isInheritedSettings =
    !orgData.team?.primary_domain &&
    !!orgData.team?.master_organization?.primary_domain;

  // Sync team_only_invitations state with fetched org data (using effective value)
  useEffect(() => {
    if (orgData.team) {
      const hasOwnDomain = !!orgData.team.primary_domain;
      const effectiveValue = hasOwnDomain
        ? orgData.team.team_only_invitations === true
        : orgData.team.master_organization?.team_only_invitations === true;
      setTeamOnlyInvitations(effectiveValue);
    }
  }, [orgData.team]);

  if (!teamId) {
    return null;
  }

  const fetchUsers = async (_teamId: string, retryCount = 0) => {
    try {
      setUsersLoading(true);
      const [_orgData, _invitations] = await Promise.all([
        getOrganizationUsers({
          orgId: _teamId,
          withUsers: true,
        }),
        getOrganizationInvitations(_teamId).catch(() => []), // Don't fail if invitations endpoint doesn't exist yet
      ]);
      setOrgData({
        ..._orgData,
        organization_users: _.sortBy(
          _orgData.organization_users.filter((u) => u?.cirra_user_id), // Filter out invalid users
          (u) => {
            return u.cirra_user_id === TokenService.getUserEmail()
              ? ''
              : u.cirra_user_id;
          }
        ),
      });
      setPendingInvitations(_invitations);
    } catch (error) {
      // Retry logic for team switching scenarios where backend context may not be ready
      if (
        retryCount < 2 &&
        (error as Error).message?.includes('Failed to fetch organization')
      ) {
        console.log(
          `Retrying fetchUsers for team ${_teamId}, attempt ${retryCount + 1}`
        );
        setTimeout(
          () => {
            fetchUsers(_teamId, retryCount + 1);
          },
          1000 * (retryCount + 1)
        ); // Exponential backoff
        return;
      }

      Notify.error((error as Error).message || 'Failed to fetch users');
    } finally {
      setUsersLoading(false);
    }
  };

  const handlePersonalAccountsToggle = async (createPersonal: boolean) => {
    try {
      setTeamOnlyLoading(true);
      const updatedTeam = await updateTeamSettings(teamId, {
        team_only_invitations: !createPersonal,
      });
      setTeamOnlyInvitations(updatedTeam.team_only_invitations === true);
      setOrgData((prev) => ({ ...prev, team: updatedTeam }));
      Notify.success(
        createPersonal
          ? 'Personal accounts will be created for new invitees.'
          : 'New invitees on this domain will not get a personal account.'
      );
    } catch (error) {
      Notify.error(
        (error as Error).message || 'Failed to update team settings'
      );
    } finally {
      setTeamOnlyLoading(false);
    }
  };

  const showModal = () => {
    setIsModalVisible(true);
    form.resetFields();
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  const inviteUser = async (values: OrganizationUserData) => {
    setAddUserLoading(true);
    try {
      const invitation = await sendInvitation({
        orgId: teamId,
        email: values.cirra_user_id,
        role: values.role,
      });
      setIsModalVisible(false);
      setPendingInvitations((prev) => [invitation, ...prev]);

      Notify.success(
        'Invitation sent! The user will receive an email to join your team.'
      );
    } catch (error) {
      Notify.error((error as Error).message || 'Failed to send invitation');
    } finally {
      setAddUserLoading(false);
    }
  };

  const handleResendInvitation = async (invitation: TeamInvitation) => {
    try {
      setUsersLoading(true);
      const updatedInvitation = await resendInvitation(teamId, invitation.id);
      setPendingInvitations((prev) =>
        prev.map((inv) => (inv.id === invitation.id ? updatedInvitation : inv))
      );
      Notify.success('Invitation resent successfully');
    } catch (error) {
      Notify.error((error as Error).message || 'Failed to resend invitation');
    } finally {
      setUsersLoading(false);
    }
  };

  const handleWithdrawInvitation = async (invitation: TeamInvitation) => {
    try {
      const isConfirmed = await confirmAction({
        title: `Withdraw Invitation`,
        content: `Are you sure you want to withdraw the invitation to ${invitation.email}?`,
        okText: 'Withdraw',
        okType: 'danger',
      });
      if (!isConfirmed) return;

      setUsersLoading(true);
      await withdrawInvitation(teamId, invitation.id);
      setPendingInvitations((prev) =>
        prev.filter((inv) => inv.id !== invitation.id)
      );
      Notify.success('Invitation withdrawn successfully');
    } catch (error) {
      Notify.error((error as Error).message || 'Failed to withdraw invitation');
    } finally {
      setUsersLoading(false);
    }
  };

  // const handleLicenseChange = async (record: OrganizationUserData) => {
  //   const newLicense =
  //     record.license === OrganizationLicense.Full
  //       ? OrganizationLicense.Guest
  //       : OrganizationLicense.Full;
  //   try {
  //     setUsersLoading(true);
  //     const updatedUser = await updateUserLicense(newLicense, record.id);
  //     const stats = await getOrganizationStats(orgId);

  //     const userIndex = orgData.organization_users.findIndex(
  //       (user) => user.cirra_user_id === record.cirra_user_id
  //     );
  //     if (userIndex !== -1) {
  //       const updatedUsers = [...orgData.organization_users];
  //       updatedUsers[userIndex] = updatedUser;
  //       setOrgData({
  //         team: orgData.team,
  //         organization_users: updatedUsers,
  //         full_users_count: stats.full_users_count,
  //         full_users_limit: stats.full_users_limit,
  //       });
  //     }
  //     Notify.success('User license updated successfully');
  //   } catch (error) {
  //     Notify.error((error as Error).message || 'Failed to update user license');
  //   } finally {
  //     setUsersLoading(false);
  //   }
  // };

  const handleRoleChange = async (
    record: OrganizationUserData,
    newRole: UserRole
  ) => {
    try {
      setUsersLoading(true);
      const updatedUser = await updateUserRole(newRole, record.id);

      const userIndex = orgData.organization_users.findIndex(
        (user) => user.cirra_user_id === record.cirra_user_id
      );
      if (userIndex !== -1) {
        const updatedUsers = [...orgData.organization_users];
        updatedUsers[userIndex] = updatedUser;
        setOrgData({
          team: orgData.team,
          organization_users: updatedUsers,
          // full_users_count: orgData.full_users_count,
          // full_users_limit: orgData.full_users_limit,
          // full_users_remaining: orgData.full_users_remaining,
        });
      }
      Notify.success('User role updated successfully');
    } catch (error) {
      Notify.error((error as Error).message || 'Failed to update user role');
    } finally {
      setUsersLoading(false);
    }
  };

  const renderActions = (record: OrganizationUserData) => {
    // Add safety check for cirra_user_id
    if (!record?.cirra_user_id) {
      return null;
    }

    const isCurrentUser = record.cirra_user_id === TokenService.getUserEmail();

    const _genRoleActions = () => {
      if (isCurrentUser) return [];
      const hasPermissionsOnRoleField = teamAbility.can(
        'update',
        Resource.UserInfoAsUser,
        record,
        'role'
      );

      return Object.values(UserRoles)
        .filter((role) => role !== record.role)
        .map((newRole) => {
          const hasPermissions =
            hasPermissionsOnRoleField &&
            ROLE_HIERARCHY[newRole] <= ROLE_HIERARCHY[getRole()];
          return {
            label: (
              <Tooltip
                title={
                  hasPermissions
                    ? ''
                    : 'You do not have permission to change the role of this user'
                }
              >
                Change role to {getNameOfRole(newRole)}
              </Tooltip>
            ),
            key: `changeRole-${newRole}`,
            disabled: !hasPermissions,
            onClick: () => handleRoleChange(record, newRole),
          };
        });
    };

    const _genDeleteActions = () => {
      if (isCurrentUser) return [];
      const hasPermissions = teamAbility.can(
        'delete',
        Resource.UserInfoAsUser,
        record
      );

      return [
        {
          label: (
            <Tooltip
              title={
                hasPermissions
                  ? ''
                  : 'You do not have permission to delete this user'
              }
            >
              Delete user
            </Tooltip>
          ),
          key: `delete`,
          danger: true,
          disabled: !hasPermissions,
          onClick: () => handleDelete(record),
        },
      ];
    };

    const menuItems: ItemType[] = [
      // {
      //   label:
      //     record.license === OrganizationLicense.Full
      //       ? 'Change license to Guest'
      //       : 'Change license to Full',
      //   key: 'changeLicense',
      //   onClick: () => handleLicenseChange(record),
      // },
      ..._genRoleActions(),
      ..._genDeleteActions(),
    ];

    return (
      <Tooltip title={generalReadOnlyMsg}>
        <Dropdown
          menu={{
            items: menuItems,
          }}
          trigger={['click']}
        >
          <MoreOutlined />
        </Dropdown>
      </Tooltip>
    );
  };

  // Combined data type for table
  type TableRowData =
    | (OrganizationUserData & { _type: 'user' })
    | (TeamInvitation & { _type: 'invitation' });

  // Combine users and pending invitations for the table
  const tableData: TableRowData[] = [
    ...orgData.organization_users.map((user) => ({
      ...user,
      _type: 'user' as const,
    })),
    ...pendingInvitations.map((inv) => ({
      ...inv,
      _type: 'invitation' as const,
    })),
  ];

  const columns: ColumnsType<TableRowData> = [
    {
      title: 'User ID',
      key: 'cirra_user_id',
      sorter: (a, b) => {
        const aEmail = a._type === 'user' ? a.cirra_user_id : a.email;
        const bEmail = b._type === 'user' ? b.cirra_user_id : b.email;
        return (aEmail || '').localeCompare(bEmail || '');
      },
      render: (_, record) => {
        return record._type === 'user' ? record.cirra_user_id : record.email;
      },
    },
    {
      title: 'Status',
      key: 'status',
      width: 100,
      render: (_, record) => {
        if (record._type === 'invitation') {
          return (
            <InvitationStatusBadge
              status={record.status}
              expiresAt={record.expires_at}
            />
          );
        }
        return <InvitationStatusBadge status="active" />;
      },
    },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
      sorter: (a, b) => sortUserRole(a.role as UserRole, b.role as UserRole),
      render: (role: UserRole) => {
        return <RoleBadge role={role} />;
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 80,
      render: (_, record) => {
        if (record._type === 'invitation') {
          return hasInvitePermission ? renderInvitationActions(record) : null;
        }

        // Add safety check for cirra_user_id
        if (!record?.cirra_user_id) {
          return null;
        }

        const isCurrentUser =
          record.cirra_user_id === TokenService.getUserEmail();
        return isCurrentUser ? null : renderActions(record);
      },
    },
  ];

  const renderInvitationActions = (invitation: TeamInvitation) => {
    const isReadOnly = !!generalReadOnlyMsg;
    const menuItems: ItemType[] = [
      {
        label: 'Resend Invitation',
        key: 'resend',
        disabled: isReadOnly,
        onClick: () => handleResendInvitation(invitation),
      },
      {
        label: 'Withdraw Invitation',
        key: 'withdraw',
        danger: true,
        disabled: isReadOnly,
        onClick: () => handleWithdrawInvitation(invitation),
      },
    ];

    return (
      <Tooltip title={generalReadOnlyMsg}>
        <Dropdown
          menu={{ items: menuItems }}
          trigger={['click']}
          disabled={isReadOnly}
        >
          <MoreOutlined
            style={{ cursor: isReadOnly ? 'not-allowed' : 'pointer' }}
          />
        </Dropdown>
      </Tooltip>
    );
  };

  const handleDelete = async (record: OrganizationUserData) => {
    try {
      const isConfirmed = await confirmAction({
        title: `Delete User ${record.cirra_user_id}`,
        content: 'Are you sure you want to delete this user?',
        okText: 'Delete',
        okType: 'danger',
      });
      if (!isConfirmed) return;
      setUsersLoading(true);
      const data = await deleteUser(teamId, record.id);
      setOrgData(data);
      Notify.success('User deleted successfully');
    } catch (error) {
      Notify.error((error as Error).message || 'Failed to delete user');
    } finally {
      setUsersLoading(false);
    }
  };

  /*
  let licenseLabel = '';
  let licenseTooltip = '';
  if (orgData.team?.master_organization_id) {
    const labelForUsedLicenses = pluralize(orgData.full_users_count, 'license');
    licenseLabel = `${orgData.full_users_count} ${labelForUsedLicenses} allocated (${orgData.full_users_remaining}/${orgData.full_users_limit} left on parent team subscription)`;
    licenseTooltip = `You have allocated ${orgData.full_users_count} ${labelForUsedLicenses}. There are ${orgData.full_users_remaining}/${orgData.full_users_limit} licenses left on parent team subscription`;
  } else {
    licenseLabel = `${orgData.full_users_count}/${orgData.full_users_limit} licenses allocated`;
    licenseTooltip = `You have allocated ${orgData.full_users_count} out of ${orgData.full_users_limit} licenses included in your subscription.`;
  }
    */

  return (
    <div id="users-screen">
      <div className="users-screen-header">
        <div className="overview">
          {/* 
        <span className="overview-title">Overview</span>
          <span className="overview-text">{licenseLabel}</span>
          <span className="overview-info">
            <Tooltip title={licenseTooltip}>
              <QuestionCircleOutlined />
            </Tooltip>
          </span>
         */}
          <div className="overview-team-name">
            <span style={{ fontWeight: 'bold' }}>Team: </span>
            <span>{getTeamName(orgData.team)}</span>
          </div>
          {hasInvitePermission && effectivePrimaryDomain && (
            <div className="team-only-settings">
              <div className="team-only-toggle">
                <Tooltip
                  title={
                    isInheritedSettings
                      ? 'This setting is inherited from the parent team.'
                      : !isOwner
                        ? 'Only the team owner can change this setting.'
                        : 'When disabled, new invitees whose email matches the primary domain will not get a personal account. Users on other domains are unaffected.'
                  }
                >
                  <span className="team-only-label">
                    Create personal accounts for invitees?
                  </span>
                </Tooltip>
                <Switch
                  size="small"
                  checked={!teamOnlyInvitations}
                  onChange={handlePersonalAccountsToggle}
                  loading={teamOnlyLoading}
                  disabled={
                    isInheritedSettings ||
                    !isOwner ||
                    !!generalReadOnlyMsg ||
                    usersLoading
                  }
                />
                {isInheritedSettings && (
                  <span className="inherited-label">
                    (inherited from parent team)
                  </span>
                )}
              </div>
              <div className="primary-domain-display">
                <span className="primary-domain-label">Domain:</span>
                <span className="primary-domain-value">
                  {effectivePrimaryDomain}
                </span>
                {isInheritedSettings && (
                  <span className="inherited-label">
                    (inherited from parent team)
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
        {hasInvitePermission && (
          <Tooltip title={inviteTooltip}>
            <Button
              type="primary"
              onClick={showModal}
              loading={addUserLoading}
              disabled={!!inviteTooltip || addUserLoading || usersLoading}
            >
              + Invite User
            </Button>
          </Tooltip>
        )}
      </div>
      <Table<TableRowData>
        dataSource={tableData}
        rowKey={(record) => `${record._type}_${record.id}`}
        loading={globalLoading || usersLoading}
        columns={columns}
        pagination={false}
        locale={{
          emptyText: globalLoading
            ? 'Switching teams...'
            : usersLoading
              ? 'Loading users...'
              : 'No users found',
        }}
      />
      <Modal
        title="Invite User"
        open={isModalVisible}
        onCancel={handleCancel}
        footer={null}
        className="invite-user-modal"
      >
        {addUserLoading ? <div className="linear-loading"></div> : null}
        <Form
          form={form}
          onFinish={inviteUser}
          layout="vertical"
          data-hs-do-not-collect="true"
        >
          <Form.Item
            name="cirra_user_id"
            label="User Email"
            rules={[
              {
                required: true,
                type: 'email',
                message: 'Please input a valid email!',
              },
            ]}
          >
            <Input ref={teamNameInputRef} />
          </Form.Item>
          {/* <Form.Item
            name="license"
            label={
              <span className="user-info-mark">
                License
                <Tooltip title={LICENSE_INFO}>
                  <QuestionCircleOutlined />
                </Tooltip>
              </span>
            }
            initialValue={
              isLimitReached
                ? OrganizationLicense.Guest
                : OrganizationLicense.Full
            }
            rules={[{ required: true }]}
          >
            <Select
              onChange={(value) => {
                if (value === OrganizationLicense.Guest) {
                  form.setFieldValue('role', UserRoles.MEMBER);
                }
              }}
            >
              <Option
                value={OrganizationLicense.Full}
                disabled={isLimitReached}
              >
                Full
              </Option>
              <Option value={OrganizationLicense.Guest}>Guest</Option>
            </Select>
          </Form.Item> */}
          <Form.Item
            name="role"
            label="Role"
            initialValue={UserRoles.MEMBER}
            rules={[
              { required: true },
              /*
              {
                validator: (_, value) => {
                  const license = form.getFieldValue('license');
                  if (
                    license === OrganizationLicense.Guest &&
                    value === UserRoles.ADMIN
                  ) {
                    return Promise.reject(
                      ADMIN_NOT_AVAILABLE_FOR_GUEST.replace(
                        '{role}',
                        UserRoles.ADMIN
                      ).replace('{license}', OrganizationLicense.Guest)
                    );
                  }
                  return Promise.resolve();
                },
              },
              */
            ]}
          >
            <Select>
              {Object.values(UserRoles)
                .filter(
                  (role) => quantitizeRole(role) <= quantitizeRole(getRole())
                )
                .map((role) => {
                  return (
                    <Option key={role} value={role}>
                      {getNameOfRole(role)}
                    </Option>
                  );
                })}
            </Select>
          </Form.Item>
          <Form.Item className="invite-user-submit-button">
            <Button
              type="primary"
              htmlType="submit"
              loading={addUserLoading}
              disabled={addUserLoading || usersLoading}
            >
              Invite User
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default UsersScreen;
